import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../../components/dashboard-layout";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  Search,
  Filter,
  FileSpreadsheet,
  Plus,
  Calendar,
  TrendingUp,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Download,
  Check,
  X,
  Building2,
  Globe,
} from "lucide-react";
import { useAuth } from "../../../contexts/auth-context";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../../contexts/theme-provider";
import {
  handleGetReportsByUser,
  downloadOutputFile,
} from "../../../services/reports";
import type { Report } from "../../../services/reports";
import { SearchableSelect } from "../../../components/searchable-select";

export default function ReportsPage() {
  const { theme } = useTheme();
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  const [selectedReports, setSelectedReports] = useState<Set<string>>(
    new Set(),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [clientsFilter, setclientsFilter] = useState<string>("all");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [bankFilter, setBankFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [outputMode, setOutputMode] = useState<"generic" | "own">("generic");
  const isInitialMount = useRef(true);

  const [, setAvailableclients] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<{value: string, label: string}[]>([]);
  const [availableBanks, setAvailableBanks] = useState<string[]>([]);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    setMounted(true);
  }, [user]);

  useEffect(() => {
    if (!user || !mounted) return;
    setBankFilter("all");
    fetchDropdownOptions();
  }, [countryFilter]);

  useEffect(() => {
    if (!user || !mounted) return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchReports(true);
      fetchDropdownOptions();
    }
  }, [mounted]);

  useEffect(() => {
    if (!user || !mounted) return;
    if (!isInitialMount.current) {
      setFilteredReports([]);
      fetchReports(false);
    }
  }, [statusFilter, clientsFilter, countryFilter, bankFilter, startDate, endDate, searchTerm, outputMode]);

  useEffect(() => {
    if (!user || !mounted) return;
    if (!isInitialMount.current) {
      fetchReports(false);
    }
  }, [currentPage]);

  const fetchDropdownOptions = async () => {
    // Fetch all reports to populate dropdown options (not just filtered ones)
    if (!user?.id || !user?.access_token) return;

    try {
      const response = await handleGetReportsByUser(
        user.id,
        user.access_token,
        outputMode === "own",
        "all",
        "all",
        "all",
        "all",
        "",
        "",
        "",
        1000, // Get a large number to populate all options
        0,
      );

      const allReports = response.data;

      const clients = [
        ...new Set(
          allReports
            .map((r) => r.clients_name)
            .filter((o): o is string => Boolean(o) && o !== "Unknown"),
        ),
      ].sort();
      const countryMap = new Map<string, string>();
      allReports.forEach((r) => {
        if (r.country_code && r.country_name && r.country_name !== "Unknown") {
          countryMap.set(r.country_code, r.country_name);
        }
      });
      const countries = Array.from(countryMap.entries())
        .map(([code, name]) => ({ value: code, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));

      // Filter organizations by selected country
      const filteredByCountry = countryFilter === "all"
        ? allReports
        : allReports.filter((r) => r.country_code === countryFilter);

      const banks = [
        ...new Set(
          filteredByCountry
            .map((r) => r.organization_name)
            .filter((b) => b && b !== "Unknown"),
        ),
      ].sort();

      setAvailableclients(clients);
      setAvailableCountries(countries);
      setAvailableBanks(banks);
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  const fetchReports = async (isInitialLoad: boolean = false) => {
    if (!user?.id || !user?.access_token) {
      console.warn("No user available, skipping fetchReports");
      return;
    }
    try {
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        setIsTableLoading(true);
      }
      const response = await handleGetReportsByUser(
        user.id,
        user.access_token,
        outputMode === "own",
        statusFilter,
        clientsFilter,
        countryFilter,
        bankFilter,
        startDate,
        endDate,
        searchTerm,
        itemsPerPage,
        (currentPage - 1) * itemsPerPage,
      );

      const mappedReports: Report[] = response.data.map((report: any) => ({
        id: report.id.toString(),
        report_no: report.report_no,
        report_name: report.name || report.report_name || "Untitled Report",
        name: report.name,
        country: report.country_name || report.country_code || "Unknown",
        country_name: report.country_name,
        country_code: report.country_code,
        organization: report.organization_name || "Unknown",
        organization_name: report.organization_name || "",
        clients_name: report.client_name || "Unknown",
        input_file_name: report.blob_name || "N/A",
        status: report.status,
        username: report?.username || "N/A",
        timestamp: report.created_at || "",
        download_url: report.download_url,
        blob_name: report.blob_name,
        original_name: report.original_name,
      }));
      console.log('Mapped Reports:', mappedReports);
      setFilteredReports(mappedReports);
      setTotalCount(response.total);
      setSelectedReports(new Set());
    } catch (error) {
      console.error("Error fetching reports:", error);
      setFilteredReports([]);
      setTotalCount(0);
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      } else {
        setIsTableLoading(false);
      }
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getStatusBadge = (status: Report["status"]) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-semibold rounded-lg text-xs">
            Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 font-semibold rounded-lg text-xs">
            Failed
          </Badge>
        );
      default:
        return (
          <Badge
            variant="secondary"
            className="font-semibold rounded-lg text-xs"
          >
            {status}
          </Badge>
        );
    }
  };

  const handleSelectReport = (reportId: string) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(reportId)) {
      newSelected.delete(reportId);
    } else {
      newSelected.add(reportId);
    }
    setSelectedReports(newSelected);
  };

  const handleSelectAllOnPage = () => {
    const currentPageIds = new Set(filteredReports.map((r) => r.id));
    const newSelected = new Set(selectedReports);

    const allSelected = filteredReports.every((r) => newSelected.has(r.id));

    if (allSelected) {
      currentPageIds.forEach((id) => newSelected.delete(id));
    } else {
      currentPageIds.forEach((id) => newSelected.add(id));
    }

    setSelectedReports(newSelected);
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = new Set(filteredReports.map((r) => r.id));
    const newSelected = new Set(selectedReports);

    const allSelected = filteredReports.every((r) => newSelected.has(r.id));

    if (allSelected) {
      filteredIds.forEach((id) => newSelected.delete(id));
    } else {
      filteredIds.forEach((id) => newSelected.add(id));
    }

    setSelectedReports(newSelected);
  };

  const handleDownloadSelected = async () => {
    if (selectedReports.size === 0 || !user?.access_token) return;

    try {
      setIsDownloading(true);

      const reportsToDownload = filteredReports.filter(
        (r) => selectedReports.has(r.id) && r.status === "completed",
      );

      if (reportsToDownload.length === 0) {
        alert("Only completed reports can be downloaded.");
        setIsDownloading(false);
        return;
      }

      const downloadResults: Array<{
        report: Report;
        success: boolean;
        error?: string;
      }> = [];

      for (const report of reportsToDownload) {
        try {
          await downloadOutputFile(
            report.id,
            user.id,
            report.original_name || `${report.report_name}.docx`,
            user.access_token,
          );
          downloadResults.push({ report, success: true });
        } catch (error) {
          console.error(
            `Failed to download report ${report.report_name}:`,
            error,
          );
          downloadResults.push({
            report,
            success: false,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }

      const successCount = downloadResults.filter((r) => r.success).length;
      const errorCount = downloadResults.filter((r) => !r.success).length;

      if (errorCount === 0) {
        alert(`✅ Successfully downloaded all ${successCount} report(s)`);
      } else if (successCount === 0) {
        const failedReportNames = downloadResults
          .filter((r) => !r.success)
          .map((r) => `"${r.report.report_name}"`)
          .join(", ");
        alert(
          `❌ Failed to download all ${errorCount} report(s):\n${failedReportNames}`,
        );
      } else {
        const successReportNames = downloadResults
          .filter((r) => r.success)
          .map((r) => `"${r.report.report_name}"`)
          .join(", ");
        const failedReportNames = downloadResults
          .filter((r) => !r.success)
          .map((r) => `"${r.report.report_name}"`)
          .join(", ");
        alert(
          `⚠️ Partial success:\n✅ Downloaded ${successCount} report(s): ${successReportNames}\n❌ Failed ${errorCount} report(s): ${failedReportNames}`,
        );
      }
    } catch (error) {
      console.error("Error in download operation:", error);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedReports(new Set());
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setclientsFilter("all");
    setCountryFilter("all");
    setBankFilter("all");
    setStartDate("");
    setEndDate("");
  };

  const activeFiltersCount = [
    searchTerm !== "",
    statusFilter !== "all",
    clientsFilter !== "all",
    countryFilter !== "all",
    bankFilter !== "all",
    startDate !== "",
    endDate !== "",
  ].filter(Boolean).length;

  if (!mounted) return null;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-8 text-center min-h-screen flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 2,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
            className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full"
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div
        className={`p-4 md:p-8 min-h-screen ${theme === "dark" ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" : "bg-gradient-to-br from-slate-50 via-white to-blue-50"}`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="w-full sm:w-auto">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`text-2xl md:text-3xl font-black bg-clip-text text-transparent ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-white via-blue-100 to-purple-100"
                    : "bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900"
                }`}
              >
                Reports
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`font-medium mt-1 text-sm ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}
              >
                View and manage your generated analysis reports
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="w-full sm:w-auto"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex justify-end sm:justify-start"
              >
                <Button
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/25 rounded-xl w-full sm:w-auto text-white text-sm h-9 cursor-pointer"
                  onClick={() => navigate("/user/dashboard")}
                >
                  <Plus className="h-3.5 w-3.5 mr-2" />
                  Create New Report
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-3 gap-3"
          >
            {[
              {
                icon: FileSpreadsheet,
                label: "Total",
                value: totalCount,
                color: "from-blue-500 to-cyan-600",
                bgColor: `${theme === "dark" ? "from-blue-950/30 to-cyan-950/30" : "from-blue-50 dark:from-blue-900/30 to-cyan-50 dark:to-cyan-900/30"}`,
              },
              {
                icon: TrendingUp,
                label: "Completed",
                value: filteredReports.filter((r: Report) => r.status === "completed").length,
                color: "from-green-500 to-emerald-600",
                bgColor: `${theme === "dark" ? "from-green-950/30 to-emerald-950/30" : "from-green-50 dark:from-green-900/30 to-emerald-50 dark:to-emerald-900/30"}`,
              },
              {
                icon: Calendar,
                label: "This Month",
                value: filteredReports.filter(
                  (r: Report) =>
                    new Date(r.timestamp).getMonth() === new Date().getMonth(),
                ).length,
                color: "from-purple-500 to-pink-600",
                bgColor: `${theme === "dark" ? "from-purple-950/30 to-pink-950/30" : "from-purple-50 dark:from-purple-900/30 to-pink-50 dark:to-pink-900/30"}`,
              },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
              >
                <Card
                  className={`bg-gradient-to-br ${stat.bgColor} border-0 shadow-sm ${theme === "dark" ? "shadow-black/10" : "shadow-black/5"} h-full`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p
                          className={`text-xs font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} mb-1`}
                        >
                          {stat.label}
                        </p>
                        <p
                          className={`text-2xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}
                        >
                          {stat.value}
                        </p>
                      </div>
                      <div
                        className={`w-8 h-8 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center shadow-sm`}
                      >
                        <stat.icon className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Reports Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Card
              className={`backdrop-blur-xl shadow-lg md:shadow-xl ${theme === "dark" ? "bg-slate-800/70 shadow-black/20 border-slate-700/50" : "bg-white/70 shadow-black/5 border-slate-200/50"}`}
            >
              <CardHeader className="p-4 md:p-6 pb-3">
                <div className="flex flex-col gap-4">
                  {/* Title and Batch Controls Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{
                          duration: 4,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "linear",
                        }}
                        className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center"
                      >
                        <Sparkles className="h-3 w-3 text-white" />
                      </motion.div>
                      <CardTitle
                        className={`text-lg md:text-xl font-black ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}
                      >
                        Reports ({filteredReports.length})
                      </CardTitle>
                    </div>

                    {selectedReports.size > 0 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-2"
                      >
                        <Badge variant="secondary" className="text-xs">
                          {selectedReports.size} selected
                        </Badge>
                        <Button
                          size="sm"
                          onClick={handleDownloadSelected}
                          className="h-8 text-xs bg-green-600 hover:bg-green-700"
                          disabled={
                            !filteredReports.some(
                              (r) =>
                                selectedReports.has(r.id) &&
                                r.status === "completed",
                            ) || isDownloading
                          }
                        >
                          {isDownloading ? (
                            <div className="h-3.5 w-3.5 mr-1.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          {isDownloading
                            ? "Downloading..."
                            : "Download Selected"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearSelection}
                          className="h-8 text-xs"
                        >
                          <X className="h-3.5 w-3.5 mr-1.5" />
                          Clear
                        </Button>
                      </motion.div>
                    )}
                  </div>

                  {/* Search and Filters Row */}
                  <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        placeholder="Search reports, organizations, countries..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`pl-9 rounded-lg text-sm h-8 ${
                          theme === "dark"
                            ? "bg-slate-700/60 border-slate-600 text-white placeholder:text-slate-400"
                            : "bg-white border-slate-200/70 text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                        }`}
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {/* Output Mode Filter */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className={`h-8 cursor-pointer rounded-lg text-xs ${
                              theme === "dark"
                                ? "bg-slate-700/60 border-slate-600 text-white hover:bg-slate-600 hover:text-white"
                                : "bg-white border-slate-200/70 text-slate-800 dark:text-slate-100 hover:bg-slate-50"
                            } ${outputMode !== "generic" ? "border-blue-500" : ""}`}
                          >
                            <Filter className="h-3.5 w-3.5 mr-1.5" />
                            {outputMode === "generic" ? "All" : "My"}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          className={`backdrop-blur-xl transition-colors ${
                            theme === "dark"
                              ? "bg-slate-800 border-slate-700"
                              : "bg-white border-slate-200"
                          }`}
                        >
                          <DropdownMenuLabel className={`text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
                            Reports Generated By
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator className={theme === "dark" ? "bg-slate-700" : "bg-slate-200 dark:bg-slate-800"} />
                          <DropdownMenuItem
                            onClick={() => setOutputMode("generic")}
                            className={`text-xs cursor-pointer ${
                              theme === "dark"
                                ? "text-slate-200 hover:bg-slate-700 focus:bg-slate-700 focus:text-white"
                                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 focus:bg-slate-100"
                            }`}
                          >
                            All
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setOutputMode("own")}
                            className={`text-xs cursor-pointer ${
                              theme === "dark"
                                ? "text-slate-200 hover:bg-slate-700 focus:bg-slate-700 focus:text-white"
                                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 focus:bg-slate-100"
                            }`}
                          >
                            My
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {/* Status Filter */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className={`h-8 cursor-pointer rounded-lg text-xs ${
                              theme === "dark"
                                ? "bg-slate-700/60 border-slate-600 text-white hover:bg-slate-600 hover:text-white"
                                : "bg-white border-slate-200/70 text-slate-800 dark:text-slate-100 hover:bg-slate-50"
                            } ${statusFilter !== "all" ? "border-blue-500" : ""}`}
                          >
                            <Filter className="h-3.5 w-3.5 mr-1.5" />
                            {statusFilter === "all" ? "Status" : statusFilter}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          className={`backdrop-blur-xl transition-colors ${
                            theme === "dark"
                              ? "bg-slate-800 border-slate-700"
                              : "bg-white border-slate-200"
                          }`}
                        >
                          <DropdownMenuLabel className={`text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
                            Filter by Status
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator className={theme === "dark" ? "bg-slate-700" : "bg-slate-200 dark:bg-slate-800"} />
                          <DropdownMenuItem
                            onClick={() => setStatusFilter("all")}
                            className={`text-xs cursor-pointer ${
                              theme === "dark"
                                ? "text-slate-200 hover:bg-slate-700 focus:bg-slate-700 focus:text-white"
                                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 focus:bg-slate-100"
                            }`}
                          >
                            All Status
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setStatusFilter("completed")}
                            className={`text-xs cursor-pointer ${
                              theme === "dark"
                                ? "text-slate-200 hover:bg-slate-700 focus:bg-slate-700 focus:text-white"
                                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 focus:bg-slate-100"
                            }`}
                          >
                            Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setStatusFilter("failed")}
                            className={`text-xs cursor-pointer ${
                              theme === "dark"
                                ? "text-slate-200 hover:bg-slate-700 focus:bg-slate-700 focus:text-white"
                                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 focus:bg-slate-100"
                            }`}
                          >
                            Failed
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {/* Country Filter */}
                      <div className="relative">
                          <SearchableSelect
                            options={[
                              { value: "all", label: "All Countries" },
                              ...availableCountries,
                            ]}
                            value={countryFilter}
                            onChange={(value) => setCountryFilter(value)}
                            placeholder="Select country"
                            theme={theme}
                            inputStyleProp="w-50 overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md"
                            listStyleProp="p-2 text-sm cursor-pointer transition-colors"
                          />
                        </div>

                      {/* Bank Filter */}
                      <div className="relative">
                          <SearchableSelect
                            options={[
                              { value: "all", label: "All Organizations" },
                              ...availableBanks.map((bank) => ({
                                value: bank,
                                label: bank,
                              })),
                            ]}
                            value={bankFilter}
                            onChange={(value) => setBankFilter(value)}
                            placeholder="Select bank"
                            theme={theme}
                            inputStyleProp="w-50 overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md"
                            listStyleProp="p-2 text-sm cursor-pointer transition-colors"
                          />
                        </div>

                      {/* Date Range */}
                      <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1.5 border rounded-lg px-2 py-1 ${
                          theme === "dark"
                            ? "bg-slate-700/60 border-slate-600"
                            : "bg-white border-slate-200/70"
                        }`}>
                          <Calendar className={`h-3.5 w-3.5 shrink-0 ${
                            theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                          }`} />
                          <div className="flex flex-col">
                            <span className={`text-[10px] leading-none mb-0.5 ${
                              theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                            }`}>From</span>
                            <input
                              type="month"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              className={`text-xs bg-transparent outline-none border-none w-28 ${
                                theme === "dark" ? "text-white [color-scheme:dark]" : "text-slate-800 dark:text-slate-100"
                              }`}
                            />
                          </div>
                        </div>
                        <span className={`text-xs font-medium ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                        }`}>→</span>
                        <div className={`flex items-center gap-1.5 border rounded-lg px-2 py-1 ${
                          theme === "dark"
                            ? "bg-slate-700/60 border-slate-600"
                            : "bg-white border-slate-200/70"
                        }`}>
                          <Calendar className={`h-3.5 w-3.5 shrink-0 ${
                            theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                          }`} />
                          <div className="flex flex-col">
                            <span className={`text-[10px] leading-none mb-0.5 ${
                              theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                            }`}>To</span>
                            <input
                              type="month"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              className={`text-xs bg-transparent outline-none border-none w-28 ${
                                theme === "dark" ? "text-white [color-scheme:dark]" : "text-slate-800 dark:text-slate-100"
                              }`}
                            />
                          </div>
                        </div>
                      </div>

                      {activeFiltersCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleClearFilters}
                          className="h-8 text-xs"
                        >
                          <X className="h-3.5 w-3.5 mr-1.5" />
                          Clear ({activeFiltersCount})
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                <AnimatePresence mode="wait">
                  {isTableLoading ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-center py-12 px-4"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "linear",
                        }}
                        className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"
                      />
                      <p
                        className={`text-sm font-medium ${theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}
                      >
                        Loading reports...
                      </p>
                    </motion.div>
                  ) : filteredReports.length === 0 ? (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      className="text-center py-12 px-4"
                    >
                      <FileSpreadsheet
                        className={`h-12 w-12 mx-auto mb-3 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}
                      />
                      <p
                        className={`text-base font-semibold ${theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}
                      >
                        No reports found
                      </p>
                      <p
                        className={`text-sm mt-1 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}
                      >
                        {searchTerm ||
                        statusFilter !== "all" ||
                        clientsFilter !== "all" ||
                        countryFilter !== "all" ||
                        bankFilter !== "all" ||
                        startDate ||
                        endDate
                          ? "Try adjusting your search or filters"
                          : "Create your first report to get started"}
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="table"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-x-auto"
                    >
                      <Table className="min-w-[1000px] md:min-w-full">
                        <TableHeader
                          className={
                            theme === "dark"
                              ? "bg-slate-700/30"
                              : "bg-slate-50/50 dark:bg-slate-900/50"
                          }
                        >
                          <TableRow
                            className={`${theme === "dark" ? "border-slate-700" : "border-slate-200"}`}
                          >
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3 w-10`}
                            >
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    filteredReports.length > 0 &&
                                    filteredReports.every((r) =>
                                      selectedReports.has(r.id),
                                    )
                                  }
                                  onChange={handleSelectAllOnPage}
                                  className={`h-4 w-4 rounded ${
                                    theme === "dark"
                                      ? "bg-slate-700 border-slate-600 text-blue-500 focus:ring-blue-600"
                                      : "bg-white border-slate-300 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                                  }`}
                                  title="Select all on page"
                                />
                              </div>
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              Report #
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              Report Name
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              Generated By
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              <div className="flex items-center gap-1">
                                <Building2 className="h-3.5 w-3.5" />
                                Organization
                              </div>
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              <div className="flex items-center gap-1">
                                <Globe className="h-3.5 w-3.5" />
                                Country
                              </div>
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              Status
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              Created
                            </TableHead>
                          </TableRow>
                        </TableHeader>

                        <TableBody>
                          {filteredReports.map((report: Report, index: number) => (
                            <motion.tr
                              key={report.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                delay: index * 0.05,
                                duration: 0.3,
                              }}
                              className={`transition-all duration-200 border-slate-200 ${theme === "dark" ? "dark:border-slate-700" : ""} ${
                                selectedReports.has(report.id)
                                  ? theme === "dark"
                                    ? "bg-blue-900/20"
                                    : "bg-blue-50"
                                  : theme === "dark"
                                    ? "dark:hover:bg-slate-700/30"
                                    : "hover:bg-slate-50/50"
                              }`}
                            >
                              <TableCell className="py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedReports.has(report.id)}
                                  onChange={() => handleSelectReport(report.id)}
                                  className={`h-4 w-4 rounded ${
                                    theme === "dark"
                                      ? "bg-slate-700 border-slate-600 text-blue-500 focus:ring-blue-600"
                                      : "bg-white border-slate-300 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                                  }`}
                                />
                              </TableCell>
                              <TableCell
                                className={`font-bold text-xs ${theme === "dark" ? "text-blue-400 hover:text-blue-300" : "text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"} py-3 cursor-pointer`}
                                onClick={() =>
                                  navigate(`/user/reports/${report.id}`)
                                }
                              >
                                {report.report_no}
                              </TableCell>

                              <TableCell
                                className={`font-semibold text-xs ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"} py-3 truncate max-w-[200px] cursor-pointer`}
                                onClick={() =>
                                  navigate(`/user/reports/${report.id}`)
                                }
                              >
                                {report.report_name}
                              </TableCell>
                              
                              <TableCell
                                className={`font-semibold text-xs ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"} py-3 truncate max-w-[200px] cursor-pointer`}
                                onClick={() =>
                                  navigate(`/user/reports/${report.id}`)
                                }
                              >
                                {report.username}
                              </TableCell>

                              <TableCell
                                className={`font-medium text-xs py-3 cursor-pointer ${
                                  !report.organization_name
                                    ? theme === "dark"
                                      ? "text-slate-500 italic"
                                      : "text-slate-400 italic"
                                    : theme === "dark"
                                      ? "text-slate-300"
                                      : "text-slate-700"
                                }`}
                                onClick={() =>
                                  navigate(`/user/reports/${report.id}`)
                                }
                              >
                                <div className="flex items-center gap-1.5">
                                  <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span
                                    className="truncate max-w-[150px]"
                                    title={report.organization_name}
                                  >
                                    {report.organization_name}
                                  </span>
                                </div>
                              </TableCell>

                              <TableCell
                                className={`font-medium text-xs py-3 cursor-pointer ${
                                  !report.country_name
                                    ? theme === "dark"
                                      ? "text-slate-500 italic"
                                      : "text-slate-400 italic"
                                    : theme === "dark"
                                      ? "text-slate-300"
                                      : "text-slate-700"
                                }`}
                                onClick={() =>
                                  navigate(`/user/reports/${report.id}`)
                                }
                              >
                                <div className="flex items-center gap-1.5">
                                  <Globe className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span
                                    className="truncate max-w-[120px]"
                                    title={report.country_name}
                                  >
                                    {report.country_name}
                                  </span>
                                </div>
                              </TableCell>

                              <TableCell className="py-3">
                                {getStatusBadge(report.status)}
                              </TableCell>

                              <TableCell
                                className={`font-medium text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600 dark:text-slate-400"} py-3 cursor-pointer`}
                                onClick={() =>
                                  navigate(`/user/reports/${report.id}`)
                                }
                              >
                                {new Date(
                                  report.timestamp,
                                ).toLocaleDateString()}
                              </TableCell>
                            </motion.tr>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Selection Summary and Pagination */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 md:px-6 py-3 border-t border-slate-200 dark:border-slate-700 gap-3"
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <div
                            className={`text-xs font-medium ${theme === "dark" ? "text-slate-400" : "text-slate-600 dark:text-slate-400"}`}
                          >
                            {selectedReports.size > 0 ? (
                              <div className="flex items-center gap-2">
                                <span>
                                  {selectedReports.size} report(s) selected
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleSelectAllFiltered}
                                  className="h-6 text-xs"
                                >
                                  {filteredReports.every((r) =>
                                    selectedReports.has(r.id),
                                  )
                                    ? "Deselect all"
                                    : "Select all filtered"}
                                </Button>
                                {filteredReports.every((r) =>
                                  selectedReports.has(r.id),
                                ) && (
                                  <Badge variant="outline" className="text-xs">
                                    <Check className="h-3 w-3 mr-1" />
                                    All filtered
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span>
                                Showing{" "}
                                {Math.min(
                                  currentPage * itemsPerPage,
                                  filteredReports.length,
                                )}{" "}
                                of {filteredReports.length} reports
                              </span>
                            )}
                          </div>
                        </div>

                        {totalPages > 1 && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handlePreviousPage}
                              disabled={currentPage === 1}
                              className={`h-7 text-xs ${theme === "dark" ? "border-slate-600" : ""}`}
                            >
                              <ChevronLeft className="h-3 w-3 mr-1" />
                              Previous
                            </Button>
                            <div
                              className={`text-xs font-medium px-2 ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"}`}
                            >
                              {currentPage} / {totalPages}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleNextPage}
                              disabled={currentPage === totalPages}
                              className={`h-7 text-xs ${theme === "dark" ? "border-slate-600" : ""}`}
                            >
                              Next
                              <ChevronRight className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        )}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
